/**
 * API Client (Axios)
 * CCH Care - Version 1.0 → 1.3
 *
 * Changes in 1.3 (2026-05-13) — 403 access-denied handling:
 * - 403 responses now fire a 'care:access-denied' custom DOM event
 *   carrying the request URL, method, error message, and (when
 *   present) an audit-style code like CARE_PATIENT_SCOPE_DENIED or
 *   CARE_PERMISSION_DENIED. AccessDeniedHandler.jsx (mounted by
 *   AppShell) listens for this event and surfaces a toast + redirect.
 * - The promise still rejects after the event fires, so existing
 *   callers that .catch() on 403 still work. The global handler
 *   layers on top of caller-level handling, not in place of it.
 * - Previously: 403 fell through to the generic error message
 *   normalization, which meant the caller saw a rejected promise but
 *   the UI had no awareness — silent picker fallback, blank pages,
 *   etc. The smoke test on 2026-05-12 (Sarah hitting /dashboard/adl/4)
 *   exposed this gap directly.
 *
 * Changes in 1.1:
 * - 401 no longer hard-redirects to /login
 * - Instead fires a 'care:session-expired' custom DOM event
 * - Failed requests are queued and retried automatically after re-auth
 * - If re-auth is cancelled/fails, all queued requests are rejected
 *   and THEN the hard redirect happens
 *
 * Changes in 1.2 (2026-04-19) — Remember Me:
 * - Auth reads fall back through localStorage → sessionStorage so the
 *   interceptor works for both remembered (localStorage) and ephemeral
 *   (sessionStorage) sessions.
 * - onReAuth writes the refreshed token back to whichever store holds
 *   the current session (so a remembered session stays remembered).
 * - onCancel clears both stores.
 *
 * Note on getters: we inline the dual-store lookup here rather than
 * importing from useAuth.js to avoid a circular dep (client.js ← api/auth.js
 * ← useAuth.js ← client.js via imports).
 */

import axios from 'axios';
import API_BASE_URL from '../config/api';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// ── Auth storage helpers (dual-store) ─────────────────────────
// Mirrors the read/write logic in useAuth.js. Kept here as a local
// copy to avoid circular module imports.

const AUTH_KEYS = ['care_token', 'care_membership_id', 'care_tenant_id', 'care_facility_id', 'care_last_activity'];

function readAuthField(key) {
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Returns the store that currently holds the session token, or null. */
function activeAuthStore() {
  try {
    if (localStorage.getItem('care_token'))   return localStorage;
    if (sessionStorage.getItem('care_token')) return sessionStorage;
  } catch {}
  return null;
}

function clearAllAuthStorage() {
  AUTH_KEYS.forEach(k => {
    try { localStorage.removeItem(k); }   catch {}
    try { sessionStorage.removeItem(k); } catch {}
  });
}

// ── Re-auth queue ─────────────────────────────────────────────
// When a 401 fires mid-flight, all subsequent requests queue here
// until re-auth resolves or cancels.
let isHandling401  = false;
let pendingQueue   = []; // [{ resolve, reject }]

function flushQueue(newToken) {
  pendingQueue.forEach(p => p.resolve(newToken));
  pendingQueue = [];
}

function rejectQueue(err) {
  pendingQueue.forEach(p => p.reject(err));
  pendingQueue = [];
}

// ── Request interceptor — inject auth headers ─────────────────
client.interceptors.request.use(
  (config) => {
    const token      = readAuthField('care_token');
    const membership = readAuthField('care_membership_id');
    const tenant     = readAuthField('care_tenant_id');
    const facility   = readAuthField('care_facility_id');

    if (token)      config.headers['Authorization']   = `Bearer ${token}`;
    if (membership) config.headers['X-Membership-ID'] = membership;
    if (tenant)     config.headers['X-Tenant-ID']     = tenant;
    if (facility)   config.headers['X-Facility-ID']   = facility;

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor — handle 401 + 403 ───────────────────
client.interceptors.response.use(
  (response) => response,
  (error) => {

    // 401 from a credential endpoint = bad code/password, NOT an expired
    // session. Routing it through the re-auth flow hangs the request,
    // because that flow waits for SessionTimeout (mounted only in AppShell,
    // not on /login) to resolve it. Let these reject normally so the
    // caller's catch/finally run. session.php and other in-session 401s
    // still use the re-auth flow as before.
    const reqUrl = error.config?.url || '';
    const CREDENTIAL_ENDPOINTS = [
      '/auth/login.php',
      '/auth/verify-2fa.php',
      '/auth/forgot-password.php',
      '/auth/reset-password.php',
      '/auth/reauth.php',
    ];
    const isCredentialEndpoint = CREDENTIAL_ENDPOINTS.some(p => reqUrl.includes(p));

    // ── 401: session expired → re-auth flow (unchanged) ─────────
    if (error.response?.status === 401 && !isCredentialEndpoint) {

      // If we're already handling a 401, queue this request to retry later
      if (isHandling401) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({
            resolve: (newToken) => {
              error.config.headers['Authorization'] = `Bearer ${newToken}`;
              resolve(client(error.config));
            },
            reject,
          });
        });
      }

      isHandling401 = true;

      // Return a Promise that resolves/rejects based on re-auth outcome
      return new Promise((resolve, reject) => {
        window.dispatchEvent(new CustomEvent('care:session-expired', {
          detail: {
            /**
             * Called by SessionTimeout after successful re-auth.
             * Retries the original request + all queued requests.
             * Writes the refreshed token to whichever store currently
             * holds the session (preserves Remember Me state).
             */
            onReAuth: (newToken) => {
              const store = activeAuthStore() || localStorage;
              try { store.setItem('care_token', newToken); } catch {}
              flushQueue(newToken);
              isHandling401 = false;
              error.config.headers['Authorization'] = `Bearer ${newToken}`;
              resolve(client(error.config));
            },

            /**
             * Called if user cancels or re-auth fails.
             * Clears auth from BOTH stores and redirects to login.
             */
            onCancel: () => {
              rejectQueue(error);
              isHandling401 = false;
              clearAllAuthStorage();
              if (window.location.pathname !== '/login') {
                window.location.href = '/login?expired=1';
              }
              reject(error);
            },
          },
        }));
      });
    }

    // ── 403: access denied → notify globally + reject ───────────
    // Unlike 401, 403 is not recoverable through retry. Fire an event
    // so AccessDeniedHandler can toast + redirect, then let the
    // promise reject normally so per-caller .catch() blocks still run.
    //
    // Opt-out: callers can pass { meta: { silentOn403: true } } on
    // the axios config to suppress the global event — useful for
    // probe-style requests that intentionally test permissions and
    // handle 403 locally (e.g. role discovery on first load).
    if (error.response?.status === 403 && !error.config?.meta?.silentOn403) {
      const data = error.response?.data || {};
      const msg  = data.error || data.message || 'Access denied';

      window.dispatchEvent(new CustomEvent('care:access-denied', {
        detail: {
          status:        403,
          message:       msg,
          code:          data.code || null, // 'CARE_PATIENT_SCOPE_DENIED' / 'CARE_PERMISSION_DENIED' / etc.
          requestUrl:    error.config?.url || null,
          requestMethod: error.config?.method?.toUpperCase() || null,
        },
      }));
      // fall through to standard rejection below
    }

    // Normalize error message for all other errors
    const message =
      error.response?.data?.error  ||
      error.response?.data?.message ||
      error.message                 ||
      'An unexpected error occurred';

    return Promise.reject({ ...error, message });
  }
);

export default client;